/**
 * Verification Badge Component
 *
 * Displays broker/company verification badges with different tiers:
 * - Verified Broker (وسيط موثوق) - Blue
 * - Gold Broker (وسيط ذهبي) - Gold
 * - Verified Company (منشأة معتمدة) - Purple
 */

import { useTranslation } from 'react-i18next';
import Badge from '../base/Badge';

export type BadgeTier = 'verified' | 'gold' | 'platinum' | 'company';

export interface VerificationBadgeProps {
  tier: BadgeTier;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showTooltip?: boolean;
  className?: string;
}

const badgeConfig: Record<BadgeTier, {
  variant: 'verified' | 'gold' | 'platinum' | 'premium';
  icon: string;
  labelKey: string;
  tooltipKey: string;
}> = {
  verified: {
    variant: 'verified',
    icon: 'ri-verified-badge-fill',
    labelKey: 'badges.verified',
    tooltipKey: 'badges.verifiedTooltip',
  },
  gold: {
    variant: 'gold',
    icon: 'ri-vip-crown-fill',
    labelKey: 'badges.gold',
    tooltipKey: 'badges.goldTooltip',
  },
  platinum: {
    variant: 'platinum',
    icon: 'ri-vip-diamond-fill',
    labelKey: 'badges.platinum',
    tooltipKey: 'badges.platinumTooltip',
  },
  company: {
    variant: 'premium',
    icon: 'ri-building-fill',
    labelKey: 'badges.company',
    tooltipKey: 'badges.companyTooltip',
  },
};

export default function VerificationBadge({
  tier,
  size = 'md',
  showLabel = true,
  showTooltip = true,
  className = '',
}: VerificationBadgeProps) {
  const { t } = useTranslation('verification');
  const config = badgeConfig[tier];

  return (
    <div className={`inline-flex items-center group relative ${className}`}>
      <Badge
        variant={config.variant}
        icon={config.icon}
        size={size}
      >
        {showLabel && t(config.labelKey)}
      </Badge>

      {showTooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
          {t(config.tooltipKey)}
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
        </div>
      )}
    </div>
  );
}

// Pre-configured badge exports for convenience
export function VerifiedBrokerBadge({ className = '' }: { className?: string }) {
  return <VerificationBadge tier="verified" className={className} />;
}

export function GoldBrokerBadge({ className = '' }: { className?: string }) {
  return <VerificationBadge tier="gold" className={className} />;
}

export function PlatinumBrokerBadge({ className = '' }: { className?: string }) {
  return <VerificationBadge tier="platinum" className={className} />;
}

export function VerifiedCompanyBadge({ className = '' }: { className?: string }) {
  return <VerificationBadge tier="company" className={className} />;
}
